/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

export type MergeDeepInput = Record<string, unknown>;

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */

export const isObject = (item: unknown): item is Record<string, unknown> => {
  return !!item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 * @returns {object}
 */
export const mergeDeep = <T extends MergeDeepInput>(
  target: MergeDeepInput = {},
  ...sources: MergeDeepInput[]
): T => {
  if (!sources.length) {
    return target as T;
  }
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }
        mergeDeep(target[key] as MergeDeepInput, source[key] as MergeDeepInput);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
};

export const deepEquals = (object1: Record<string, unknown>, object2: Record<string, unknown>) => {
  const obj1Keys = Object.keys(object1);
  const obj2Keys = Object.keys(object2);

  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }

  for (const key of obj1Keys) {
    const value1 = object1[key];
    const value2 = object2[key];

    const areObjects = isObject(value1) && isObject(value2);

    if ((areObjects && !deepEquals(value1, value2)) || (!areObjects && value1 !== value2)) {
      return false;
    }
  }

  return true;
};
